import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { VEHICLE_LABELS, vehicleLabel } from '../lib/logisticsUi';
import './DashboardPages.css';

const EMPTY_DRIVER = {
  name: '',
  phone: '',
  vehicle_type: 'pickup',
  vehicle_plate: '',
  capacity_kg: '',
  cold_chain_capable: false,
  active: true,
  pin: '',
};

function DriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [createForm, setCreateForm] = useState(EMPTY_DRIVER);
  const [creating, setCreating] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_DRIVER);
  const [savingEdit, setSavingEdit] = useState(false);

  const loadDrivers = async () => {
    try {
      setLoading(true);
      setError(null);
      const { drivers: data } = await api.getDrivers();
      setDrivers(data);
    } catch (err) {
      setError(err.message);
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDrivers();
  }, []);

  const extraFields = (form) => ({
    vehicle_type: form.vehicle_type,
    vehicle_plate: form.vehicle_plate.trim() || null,
    capacity_kg: form.capacity_kg === '' ? undefined : form.capacity_kg,
    cold_chain_capable: form.cold_chain_capable,
    active: form.active,
    pin: form.pin.trim() || undefined,
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      setError(null);
      setSuccess(null);
      const name = createForm.name.trim();
      await api.createDriver(name, createForm.phone.trim(), extraFields(createForm));
      setCreateForm(EMPTY_DRIVER);
      setSuccess(`Driver "${name}" added successfully.`);
      await loadDrivers();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (driver) => {
    setEditModal(driver);
    setEditForm({
      name: driver.name,
      phone: driver.phone,
      vehicle_type: driver.vehicle_type || 'pickup',
      vehicle_plate: driver.vehicle_plate || '',
      capacity_kg: driver.capacity_kg != null ? String(driver.capacity_kg) : '',
      cold_chain_capable: Boolean(driver.cold_chain_capable),
      active: driver.active !== false,
      pin: '',
    });
  };

  const closeEdit = () => {
    if (!savingEdit) setEditModal(null);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editModal) return;
    try {
      setSavingEdit(true);
      setError(null);
      setSuccess(null);
      const payload = {
        name: editForm.name.trim(),
        phone: editForm.phone.trim(),
        vehicle_type: editForm.vehicle_type,
        vehicle_plate: editForm.vehicle_plate.trim() || null,
        capacity_kg: editForm.capacity_kg === '' ? undefined : editForm.capacity_kg,
        cold_chain_capable: editForm.cold_chain_capable,
        active: editForm.active,
      };
      if (editForm.pin.trim()) payload.pin = editForm.pin.trim();
      await api.updateDriver(editModal.driver_id, payload);
      setEditModal(null);
      setSuccess('Driver updated.');
      await loadDrivers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (driver) => {
    if (!window.confirm(`Delete driver "${driver.name}"? This cannot be undone.`)) return;
    try {
      setError(null);
      setSuccess(null);
      await api.deleteDriver(driver.driver_id);
      setEditModal(null);
      setSuccess(`Driver "${driver.name}" removed.`);
      await loadDrivers();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const driverFields = (form, setForm, prefix) => (
    <>
      <div className="form-group">
        <label htmlFor={`${prefix}-driver-name`}>Full name *</label>
        <input
          id={`${prefix}-driver-name`}
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
          placeholder="e.g. Paul Driver"
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${prefix}-driver-phone`}>WhatsApp / phone *</label>
        <input
          id={`${prefix}-driver-phone`}
          type="tel"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          required
          placeholder="+237 6XX XXX XXX"
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${prefix}-driver-vehicle`}>Vehicle</label>
        <select
          id={`${prefix}-driver-vehicle`}
          value={form.vehicle_type}
          onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
        >
          {Object.entries(VEHICLE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label htmlFor={`${prefix}-driver-plate`}>Plate</label>
        <input
          id={`${prefix}-driver-plate`}
          type="text"
          value={form.vehicle_plate}
          onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value })}
          placeholder="CM-XXXX-XX"
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${prefix}-driver-cap`}>Capacity (kg)</label>
        <input
          id={`${prefix}-driver-cap`}
          type="number"
          min="1"
          value={form.capacity_kg}
          onChange={(e) => setForm({ ...form, capacity_kg: e.target.value })}
          placeholder="Default from vehicle type"
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${prefix}-driver-pin`}>App PIN (4 digits)</label>
        <input
          id={`${prefix}-driver-pin`}
          type="password"
          inputMode="numeric"
          maxLength={4}
          autoComplete="off"
          value={form.pin}
          onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
          placeholder={prefix === 'edit' ? 'Leave blank to keep' : 'Optional'}
        />
      </div>
      <div className="form-group form-group-checks">
        <label className="ff-check">
          <input
            className="ff-check-input"
            type="checkbox"
            checked={form.cold_chain_capable}
            onChange={(e) => setForm({ ...form, cold_chain_capable: e.target.checked })}
          />
          <span className="ff-check-ui" aria-hidden="true" />
          <span className="ff-check-text">Cold-chain capable</span>
        </label>
        <label className="ff-check">
          <input
            className="ff-check-input"
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
          />
          <span className="ff-check-ui" aria-hidden="true" />
          <span className="ff-check-text">Active</span>
        </label>
      </div>
    </>
  );

  return (
    <div className="dashboard-page drivers-page">
      <h1>Drivers</h1>
      <p className="dashboard-subtitle">
        Register drivers with vehicle details and an optional 4-digit PIN for the driver app.
      </p>

      <section className="entity-manual-panel" aria-labelledby="driver-manual-heading">
        <h2 id="driver-manual-heading" className="entity-manual-title">
          Manual driver registration
        </h2>
        <p className="orders-hint entity-manual-hint">
          Use the driver&apos;s real WhatsApp number with country code (e.g. +237…). Required before
          assigning orders on the Orders page.
        </p>
        <form className="entity-manual-form" onSubmit={handleCreate}>
          <div className="entity-manual-fields">
            {driverFields(createForm, setCreateForm, 'create')}
          </div>
          <button type="submit" className="entity-add-btn" disabled={creating}>
            {creating ? 'Saving…' : 'Save new driver'}
          </button>
        </form>
      </section>

      {success && <div className="orders-success">{success}</div>}
      {error && <div className="orders-error">{error}</div>}

      <h2 className="entity-list-heading">Registered drivers</h2>

      {loading ? (
        <div className="orders-placeholder">
          <p>Loading drivers…</p>
        </div>
      ) : drivers.length === 0 ? (
        <div className="orders-placeholder">
          <p>No drivers in the list yet.</p>
          <p className="orders-hint">Add at least one driver using the form above.</p>
        </div>
      ) : (
        <div className="orders-list">
          {drivers.map((d) => (
            <div key={d.driver_id} className="order-card">
              <div className="order-card-header">
                <span className="order-id">#{d.driver_id}</span>
                {d.active === false && <span className="status-badge">Inactive</span>}
              </div>
              <div className="order-card-body">
                <p><strong>Name:</strong> {d.name}</p>
                <p><strong>Phone:</strong> {d.phone}</p>
                <p><strong>Vehicle:</strong> {vehicleLabel(d.vehicle_type)}{d.vehicle_plate ? ` · ${d.vehicle_plate}` : ''}</p>
                <p><strong>Capacity:</strong> {d.capacity_kg != null ? `${d.capacity_kg} kg` : '—'}</p>
                <p><strong>Cold chain:</strong> {d.cold_chain_capable ? 'Yes' : 'No'}</p>
                <p><strong>PIN:</strong> {d.pin_set ? 'Set' : 'Not set'}</p>
                <p><strong>Registered:</strong> {formatDate(d.created_at)}</p>
              </div>
              <div className="entity-actions">
                <button type="button" className="entity-btn entity-edit" onClick={() => openEdit(d)}>
                  Edit
                </button>
                <button type="button" className="entity-btn entity-delete" onClick={() => handleDelete(d)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editModal && (
        <div className="modal-overlay" onClick={closeEdit}>
          <div className="modal-content modal-form modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Edit driver #{editModal.driver_id}</h3>
            <form onSubmit={handleEditSave}>
              {driverFields(editForm, setEditForm, 'edit')}
              <div className="modal-actions">
                <button type="button" className="modal-close-btn" onClick={closeEdit} disabled={savingEdit}>
                  Cancel
                </button>
                <button type="submit" className="entity-btn entity-edit" disabled={savingEdit}>
                  {savingEdit ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DriversPage;
